import { useState, useEffect } from 'react';
import { getRates, createRate, updateRate, deleteRate, type Rate } from '../../api/rates.api';
import { formatCOP, CurrencyInput } from '../../utils/currencyFormat';

export default function Tarifas() {
  const [showModal, setShowModal] = useState(false);
  const [editingTarifa, setEditingTarifa] = useState<Rate | null>(null);
  const [tarifas, setTarifas] = useState<Rate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadTarifas();
  }, []);

  const loadTarifas = async () => {
    try {
        const data = await getRates();
        setTarifas(data);
    } catch (error) {
        console.error('Error loading rates:', error);
    }
  };

  const [formData, setFormData] = useState({
    tipoVehiculo: '',
    tarifaBase: '',
    minutosGratis: '',
    fraccionMinutos: '',
    valorFraccion: '',
    tarifaMaximaDia: ''
  });

  // Descripción de campos para ayuda al usuario
  const fieldDescriptions = {
    tarifaBase: 'Cobro fijo por entrada (ej: $2000). Usar 0 si solo cobra por fracciones.',
    minutosGratis: 'Tiempo de cortesía sin cobro (ej: 15 minutos)',
    fraccionMinutos: 'Cada cuántos minutos se cobra después del tiempo gratis (ej: 15 min)',
    valorFraccion: 'Valor a cobrar por cada fracción (ej: $2000 cada 15 min)',
    tarifaMaximaDia: 'Tope máximo a cobrar por día (ej: $30000). Usar 0 para sin límite.'
  };

  const handleOpenModal = (tarifa?: Rate) => {
    if (tarifa) {
      setEditingTarifa(tarifa);
      setFormData({
        tipoVehiculo: tarifa.tipoVehiculo,
        tarifaBase: tarifa.tarifaBase.toString(),
        minutosGratis: tarifa.minutosGratis.toString(),
        fraccionMinutos: tarifa.fraccionMinutos.toString(),
        valorFraccion: tarifa.valorFraccion.toString(),
        tarifaMaximaDia: tarifa.tarifaMaximaDia.toString()
      });
    } else {
      setEditingTarifa(null);
      setFormData({
        tipoVehiculo: '',
        tarifaBase: '',
        minutosGratis: '',
        fraccionMinutos: '',
        valorFraccion: '',
        tarifaMaximaDia: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
        const payload = {
            tipoVehiculo: formData.tipoVehiculo,
            tarifaBase: Number(formData.tarifaBase),
            minutosGratis: Number(formData.minutosGratis),
            fraccionMinutos: Number(formData.fraccionMinutos),
            valorFraccion: Number(formData.valorFraccion),
            tarifaMaximaDia: Number(formData.tarifaMaximaDia)
        };

        if (editingTarifa) {
            await updateRate(editingTarifa.id, payload);
        } else {
            await createRate(payload);
        }
        
        await loadTarifas();
        setShowModal(false);
    } catch (error: any) {
        console.error('Error saving rate:', error);
        alert('Error al guardar tarifa: ' + (error.response?.data?.message || error.message));
    } finally {
        setIsLoading(false);
    }
  };

  const toggleActivo = async (id: string, currentStatus: boolean) => {
      // Assuming API supports partial update or we implement a toggle endpoint.
      // For now using updateRate since I added it.
      try {
          await updateRate(id, { activo: !currentStatus } as any); // Type assertion if needed
          loadTarifas();
      } catch (error) {
          console.error('Error toggling rate:', error);
      }
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar esta tarifa?')) {
        try {
            await deleteRate(id);
            loadTarifas();
        } catch (error) {
            console.error('Error deleting rate:', error);
        }
    }
  };

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Tarifas</h1>
            <p className="text-sm text-slate-500 mt-1">Configura las tarifas por tipo de vehículo</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition whitespace-nowrap w-full sm:w-auto"
          >
            <i className="ri-add-line text-lg"></i>
            <span className="font-medium">Nueva Tarifa</span>
          </button>
        </div>

        {/* Tarifas Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {tarifas.map((tarifa) => (
            <div key={tarifa.id} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{tarifa.tipoVehiculo}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      tarifa.activo ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {tarifa.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleOpenModal(tarifa)}
                    className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(tarifa.id)}
                    className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Tarifa base</span>
                  <span className="font-semibold text-slate-800">{formatCOP(tarifa.tarifaBase)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Minutos gratis</span>
                  <span className="font-semibold text-slate-800">{tarifa.minutosGratis} min</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Fracción</span>
                  <span className="font-semibold text-slate-800">{tarifa.fraccionMinutos} min</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Valor fracción</span>
                  <span className="font-semibold text-slate-800">{formatCOP(tarifa.valorFraccion)}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                  <span className="text-sm text-slate-500">Máximo día</span>
                  <span className="font-bold text-teal-600">{formatCOP(tarifa.tarifaMaximaDia)}</span>
                </div>
              </div>

              <button
                onClick={() => toggleActivo(tarifa.id, tarifa.activo)}
                className={`w-full mt-4 py-2 rounded-xl font-medium transition whitespace-nowrap ${
                  tarifa.activo
                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    : 'bg-teal-50 text-teal-600 hover:bg-teal-100'
                }`}
              >
                {tarifa.activo ? 'Desactivar' : 'Activar'}
              </button>
            </div>
          ))}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">
                  {editingTarifa ? 'Editar Tarifa' : 'Nueva Tarifa'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Tipo de Vehículo
                  </label>
                  <select
                    value={formData.tipoVehiculo}
                    onChange={(e) => setFormData({ ...formData, tipoVehiculo: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm bg-white"
                    required
                  >
                    <option value="">Seleccionar tipo...</option>
                    <option value="AUTO">Automóvil</option>
                    <option value="MOTO">Motocicleta</option>
                    <option value="CAMIONETA">Camioneta</option>
                    <option value="CAMION">Camión</option>
                    <option value="BICICLETA">Bicicleta</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      Tarifa Base ($)
                      <div className="group relative">
                        <i className="ri-question-line text-slate-400 cursor-help"></i>
                        <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 w-64 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-xl z-10">
                          {fieldDescriptions.tarifaBase}
                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </label>
                    <CurrencyInput
                      value={Number(formData.tarifaBase) || 0}
                      onChange={(val: number) => setFormData({ ...formData, tarifaBase: val.toString() })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="2.000"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Cobro fijo por entrada</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      Minutos Gratis
                      <div className="group relative">
                        <i className="ri-question-line text-slate-400 cursor-help"></i>
                        <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 w-64 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-xl z-10">
                          {fieldDescriptions.minutosGratis}
                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </label>
                    <input
                      type="number"
                      value={formData.minutosGratis}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d+$/.test(val)) {
                            setFormData({ ...formData, minutosGratis: val });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="15"
                      min="0"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Tiempo de cortesía</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      Fracción (minutos)
                      <div className="group relative">
                        <i className="ri-question-line text-slate-400 cursor-help"></i>
                        <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 w-64 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-xl z-10">
                          {fieldDescriptions.fraccionMinutos}
                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </label>
                    <input
                      type="number"
                      value={formData.fraccionMinutos}
                      onChange={(e) => {
                         const val = e.target.value;
                         if (val === '' || /^\d+$/.test(val)) {
                             setFormData({ ...formData, fraccionMinutos: val });
                         }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="15"
                      min="0"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Cada cuántos minutos cobra</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      Valor Fracción ($)
                      <div className="group relative">
                        <i className="ri-question-line text-slate-400 cursor-help"></i>
                        <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 w-64 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-xl z-10">
                          {fieldDescriptions.valorFraccion}
                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </label>
                    <CurrencyInput
                      value={Number(formData.valorFraccion) || 0}
                      onChange={(val: number) => setFormData({ ...formData, valorFraccion: val.toString() })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="2.000"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Valor de cada fracción</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    Tarifa Máxima por Día ($)
                    <div className="group relative">
                      <i className="ri-question-line text-slate-400 cursor-help"></i>
                      <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 w-64 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-xl z-10">
                        {fieldDescriptions.tarifaMaximaDia}
                        <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                      </div>
                    </div>
                  </label>
                  <CurrencyInput
                    value={Number(formData.tarifaMaximaDia) || 0}
                    onChange={(val: number) => setFormData({ ...formData, tarifaMaximaDia: val.toString() })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="30.000 (0 = sin límite)"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Tope máximo diario</p>
                </div>

                {/* Simulador de Cálculo */}
                {formData.tarifaBase && formData.minutosGratis && formData.fraccionMinutos && formData.valorFraccion && (
                  <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 p-4 rounded-xl">
                    <p className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                      <i className="ri-calculator-line"></i>
                      Ejemplo de cálculo (35 minutos de estacionamiento):
                    </p>
                    {(() => {
                      const totalMins = 35;
                      const base = Number(formData.tarifaBase);
                      const free = Number(formData.minutosGratis);
                      const fracMins = Number(formData.fraccionMinutos);
                      const fracVal = Number(formData.valorFraccion);
                      
                      if (totalMins <= free) {
                        return <p className="text-sm text-teal-700">✅ <strong>$0</strong> - Dentro del tiempo gratis</p>;
                      }
                      
                      const chargeableMins = totalMins - free;
                      
                      // LÓGICA CORREGIDA: La tarifa base incluye la primera fracción
                      let total = 0;
                      let explanation = '';
                      
                      if (chargeableMins <= fracMins) {
                        // Caso 1: Minutos cobrables dentro de la primera fracción
                        total = base;
                        explanation = `• ${totalMins} min - ${free} min gratis = ${chargeableMins} min cobrables\n• Como ${chargeableMins} min ≤ ${fracMins} min (primera fracción), solo se cobra:\n• Tarifa Base = ${formatCOP(base)}`;
                      } else {
                        // Caso 2: Superó la primera fracción, se cobran adicionales
                        const extraMins = chargeableMins - fracMins;
                        const extraFractions = Math.ceil(extraMins / fracMins);
                        total = base + (extraFractions * fracVal);
                        explanation = `• ${totalMins} min - ${free} min gratis = ${chargeableMins} min cobrables\n• Primera fracción (${fracMins} min): Tarifa Base ${formatCOP(base)}\n• Minutos excedentes: ${chargeableMins} - ${fracMins} = ${extraMins} min\n• Fracciones adicionales: ⌈${extraMins} ÷ ${fracMins}⌉ = ${extraFractions}\n• Total: ${formatCOP(base)} + (${extraFractions} × ${formatCOP(fracVal)}) = ${formatCOP(total)}`;
                      }
                      
                      return (
                        <div className="text-xs text-teal-700 space-y-1 whitespace-pre-line">
                          {explanation.split('\n').map((line, i) => (
                            <p key={i} className={line.includes('Total:') ? 'font-bold text-sm text-teal-900 mt-2' : ''}>{line}</p>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition font-medium whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium whitespace-nowrap flex justify-center items-center gap-2"
                  >
                    {isLoading ? <i className="ri-loader-4-line animate-spin"></i> : null}
                    {editingTarifa ? 'Actualizar' : 'Crear'} Tarifa
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
  );
}
